import { Component, OnInit, ViewChild, OnDestroy, Input, AfterViewInit } from '@angular/core';
import { InventoryService } from '@app/services/inventory/inventory.service';
import { Sort } from '@angular/material/sort';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { first, skipWhile, Subscription } from 'rxjs';
import { SubSink } from "subsink";
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store'
import * as CountsActions from '@app/modules/counts/store/actions/counts.actions';
import { selectCountsCurrentIdState, selectCountsState } from '../store/selectors/counts.selectors';
import { selectSettingsState } from '@app/modules/settings/store/selectors/settings.selectors';
import { SendMailModalComponent } from '@app/shared/components/send-mail-modal/send-mail-modal.component';
import { Router } from '@angular/router';
import { LimitAccessService } from '@app/core/services/limit-access.service';
import { ProductEditComponent } from '@app/modules/products/product-edit/product-edit.component';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-inventory-counts',
  templateUrl: './inventory-counts.component.html',
  styleUrls: ['./inventory-counts.component.scss'],
})
export class InventoryCountsComponent implements OnInit, OnDestroy, AfterViewInit {
  public id;
  public settings$;
  public countActions = CountsActions;
  public inventory: any;
  public dataSource: any = [];
  public count: number;
  public sum: number;
  public hasAsteriskProducts = false;
  public countSubscription: Subscription;
  public $tmpTotal = 0.00;
  public $tmpRetailTotal = 0.00;
  public $tmpProfitTotal = 0.00;
  public sortedData = []
  public displayedColumns = [
    'prdCode',
    'prdNameEn',
    'prdCasePack',
    'invitmCount'
  ];
  public endTimeStamp: string = '';
  public isLoading: boolean = true;
  public locPrdCustomFieldLabel1$: String = 'Counting Methods';
  public locPrdCustomFieldLabel2$: String = '';
  private subs = new SubSink();
  public isFinalizedScreen = this._inventory.isFinalizeScreen();
  // public readDraftOption: boolean = false;
  // public addDraftOption: boolean = false;
  // public editDraftOption: boolean = false;

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public _inventory: InventoryService,
    private _dialogRef: MatDialogRef<InventoryCountsComponent>,
    public store$: Store<Store>,
    public _router: Router,
    private _matDialog: MatDialog,
    private _translation: TranslateService,
    private limitAccessService: LimitAccessService
  ) {
    this.subs.add(this.store$.select(selectCountsCurrentIdState).subscribe(id => this.id = id));
    this.store$.dispatch(CountsActions.loadCount({ invID: this.id, active: 'prdNameEn', direction: 'asc' }));
  }

  ngOnInit(): void {
    this.subs.add(this.store$.select(selectSettingsState).subscribe((settings) => {
      this.settings$ = settings;

      this.locPrdCustomFieldLabel1$ = settings.locPrdCustomFieldLabel1;
      if (settings.locEnableCustomField1 == 1) {
        this.displayedColumns.push('prdCustomField1');
      }

      this.locPrdCustomFieldLabel2$ = settings.locPrdCustomFieldLabel2;
      if (settings.locEnableCustomField2 == 1) {
        this.displayedColumns.push('prdCustomField2');
      }

      // A finalized count is read-only: no remove icon on its lines.
      if (!this.isFinalizedScreen) {
        this.displayedColumns.push('checkRemove');
      }
    
    }));

    this.subs.add(this.store$.select(selectCountsState).pipe(skipWhile(({ currentInventory }) => currentInventory == null)).subscribe(({ currentInventory, isLoading }) => {
      if (currentInventory && currentInventory.invItems && currentInventory.invItems.length > 0) {
        const combinedInventory = {};

        currentInventory.invItems.forEach(item => {
          if (combinedInventory[item.prdID]) {
            combinedInventory[item.prdID].invitmCount += parseFloat(item.invitmCount);
            combinedInventory[item.prdID].isCombined = true;
            this.hasAsteriskProducts = true;
          } else {
            combinedInventory[item.prdID] = {
              ...item,
              invitmCount: parseFloat(item.invitmCount),
              isCombined: false
            };
          }
        });
    
        const groupedItems = Object.values(combinedInventory);

        this.inventory = currentInventory.inv;
        this.count = currentInventory.count;
        this.sum = currentInventory.total;
        this.endTimeStamp = currentInventory.endTimeStamp;
        this.dataSource = new MatTableDataSource(groupedItems);
      
        let totalInvitmCount = 0;
        for (let i = 0; i < currentInventory.invItems.length; i++) {
            let currentItem = currentInventory.invItems[i];
            
            let count = parseFloat(currentItem.invitmCount);
            
            if (!isNaN(count)) {
                totalInvitmCount += count;
            }
        }

        this.sum = totalInvitmCount;
        this.count = groupedItems.length;
        
      }else {
        
        this.dataSource = new MatTableDataSource([]);
        this.count = 0;
        this.sum = 0;
      }

      this.isLoading = false;
    }));

    // this.limitAccessService.arraySettingAccess$.subscribe((arraySettingAccess) => {
    //   if (arraySettingAccess) {
    //     this.readDraftOption = this.limitAccessService.getAccess('counts', 'counts', '','');
    //     this.addDraftOption = this.limitAccessService.getAccess('counts', 'counts', 'add','');
    //     this.editDraftOption = this.limitAccessService.getAccess('counts', 'counts', 'finalize','');

    //     if(!this.readDraftOption){
    //       this._router.navigateByUrl("/");
    //     }

    //   }
    // });

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store$.dispatch(CountsActions.resetCurrentInventory());
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  onClose() {
    this._dialogRef.close();
  }

  generatePDF() {
    this._inventory.onCreateCountPDF(this.inventory.invID, this.sort.active ? this.sort.active : 'prdNameEn', this.sort.direction ? this.sort.direction : 'asc', this.isFinalizedScreen ? 1 : 0);
  }

  onSortData(sort: Sort) {
    // Local sorting - don't dispatch to store to avoid affecting background list
    if (!sort.active || sort.direction === '') {
      return;
    }

    const data = this.dataSource.data.slice();
    data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      return this.compare(a[sort.active], b[sort.active], isAsc);
    });
    this.dataSource.data = data;
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sendReport() {
    const dialog = this._matDialog.open(SendMailModalComponent, {
      minWidth: '30vw',
      maxWidth: '700px',
    });

    dialog.afterClosed().pipe(first()).subscribe(res => {
       if(res!=null && res.email!=null && res.email!=''){
        const data = {
          email: res.email,
          id: this.inventory.invID,
          active: this.sort.active ? this.sort.active : 'prdNameEn',
          direction: this.sort.direction ? this.sort.direction : 'asc',
          finalized: this.isFinalizedScreen ? 1 : 0,
          type: 'byCount'
        }

          this._inventory.onSendReportPDF(data);
        }
    });
  }

  onContextMenu(event, row) {

    const prdID= row.prdID;

    if (!event.target.classList.contains('last-column')) {
      
        this._matDialog.open(ProductEditComponent, {
          data:{
            prdID: prdID,
            finalized: this.isFinalizedScreen
          },
          minWidth: '60vw',
          minHeight: '600px',
          maxWidth: '75vw',
          maxHeight: '80vh',
          //panelClass: 'ProductEdit__Modal'
        });
    }else{
       this.onDelete(prdID);
    }
  }

  onDelete(prdID){
    const dialog = this._matDialog.open(ConfirmModalComponent, {
      data: {
        key: `product`,
        deleteMessage: `${this._translation.instant('deleteConfirmSimple')}`,
        deletePassword: 'DELETE'
      },
      minWidth: '30vw',
      maxWidth: '700px',
    });

    dialog.afterClosed().subscribe(res => {
      if (res == 'true') {
        this.store$.dispatch(CountsActions.deleteProduct({ invID: this.id , prdID: prdID }));
      }
    });
  }
  
}


